import { ref, computed } from 'vue';
import ru from './ru.js';
import uz from './uz.js';
import uzCyrl from './uz-cyrl.js';

/* Языки интерфейса.

   Ответ заказчика на вопрос «на каком языке должны быть экраны у рабочих»:
   «На русском и на узбекском», и отдельно — оба узбекских письма, латиница
   и кириллица. Это не прихоть: латиница официальная, но на кириллице читает
   старшая половина цеха, и заставлять человека у станка разбирать непривычное
   письмо — то же самое, что писать ему на чужом языке.

   ГРАНИЦА ПЕРЕВОДА

   Переведены экраны, которые видит рабочий: рабочее место, вход с терминала,
   вход по паролю, андон, общий каркас и подписи состояний. Конторские экраны
   (заказы, склад, аналитика, администрирование) остаются на русском — там
   работают люди, для которых русский рабочий язык, и перевод ради полноты
   съел бы время, нужное на цех.

   Механизм при этом общий: добавить конторский экран — это дописать ключи
   в три словаря, а не переделывать устройство.

   ЧЕГО ЗДЕСЬ НЕТ

   Нет подстановки склонений и множественного числа по правилам языка.
   В цеховых надписях их почти нет, а половинчатая реализация хуже честного
   отсутствия: «5 деталь» выглядит как поломка системы.

   ПРЕДУПРЕЖДЕНИЕ

   Узбекские тексты собраны из отраслевой лексики и требуют вычитки носителем
   на заводе ДО запуска. Особенно термины «брак», «дефект», «переналадка»,
   «наладчик»: в разных цехах их называют по-разному, и слово, непривычное
   смене, хуже русского слова, которое все понимают. */

export const LOCALES = [
  { code: 'ru', name: 'Русский', short: 'RU' },
  { code: 'uz', name: "O'zbekcha", short: 'UZ' },
  { code: 'uz-Cyrl', name: 'Ўзбекча', short: 'ЎЗ' },
];

const СЛОВАРИ = { ru, uz, 'uz-Cyrl': uzCyrl };
const КЛЮЧ = 'mes-locale';

const прочитать = () => {
  try {
    const v = localStorage.getItem(КЛЮЧ);
    return СЛОВАРИ[v] ? v : 'ru';
  } catch { return 'ru'; }
};

export const locale = ref(прочитать());

export function setLocale(code) {
  if (!СЛОВАРИ[code]) return;
  locale.value = code;
  try { localStorage.setItem(КЛЮЧ, code); } catch { /* приватный режим */ }
  /* lang на документе — не украшение: от него зависят переносы,
     подбор шрифта и то, как экран прочитает программа чтения. */
  document.documentElement.setAttribute('lang', code === 'ru' ? 'ru' : 'uz');
}

const поКлючу = (словарь, ключ) => ключ.split('.').reduce((o, k) => (o == null ? o : o[k]), словарь);

/* Перевод. Отсутствующий ключ отдаёт русский текст, а не пустоту и не сам
   ключ: недопереведённый экран должен оставаться рабочим. Ключ в цехе
   вместо надписи — это поломка, русская надпись — просто не переведено. */
export function t(ключ, params) {
  let s = поКлючу(СЛОВАРИ[locale.value], ключ);
  if (s == null) s = поКлючу(ru, ключ);
  if (s == null) return ключ;
  if (!params) return s;
  return String(s).replace(/\{(\w+)\}/g, (_, k) => (params[k] ?? `{${k}}`));
}

/* Реактивная обёртка для шаблонов: computed от неё пересчитывается
   при смене языка, обычная функция — нет. */
export const useI18n = () => ({
  t: (ключ, params) => { locale.value; return t(ключ, params); },
  locale,
  setLocale,
  locales: LOCALES,
  current: computed(() => LOCALES.find((l) => l.code === locale.value) || LOCALES[0]),
});

/* Подписи перечислений домена.

   Сервер отдаёт их по-русски в /meta — это одно место правды на все экраны,
   и ломать его ради перевода нельзя. Поэтому перевод накладывается сверху:
   есть ключ в словаре — берём его, нет — остаётся русская подпись сервера.
   Так новый статус, добавленный в API, сразу виден в интерфейсе, пусть
   и по-русски, а не исчезает из него до следующего перевода. */
export function tEnum(группа, ключ, запасной) {
  if (ключ == null) return запасной ?? '';
  const s = поКлючу(СЛОВАРИ[locale.value], `enum.${группа}.${ключ}`);
  return s ?? запасной ?? ключ;
}

setLocale(locale.value);
